import type { FormConfig } from '@risksmart-app/shared/forms/types';

import { SUPPORTED_DISPLAY_TYPES } from './coerceFieldValue';
import type { ActiveFormDescriptorField } from './types';

/**
 * Field keys whose config carries NO `displayType` (so they would default to
 * `text`) but are NOT scalar text fields, and must be excluded explicitly.
 * `files`/`newFiles` are the known cases. Custom attributes are handled by the
 * `CustomAttributeData` path check below. This set may need extending as the
 * feature widens beyond the Action pilot.
 */
const NON_SCALAR_FIELD_KEYS = new Set(['files', 'newFiles']);

const isSupportedType = (displayType: string): boolean =>
  (SUPPORTED_DISPLAY_TYPES as readonly string[]).includes(displayType);

/**
 * Build the prefillable-field descriptor for the active form: the shared form
 * config intersected with the fields actually mounted (`allFieldIds` from
 * `RiskSmartFormProvider`), filtered to the display types this cut can coerce.
 *
 * Pure — the publisher supplies the config, the live field ids, and the set of
 * required field ids. A field with no `displayType` defaults to `text` (matching
 * the form config's own default), EXCEPT the explicit non-scalar keys above.
 */
export const buildActiveFormDescriptor = (
  formConfig: FormConfig,
  allFieldIds: string[],
  requiredFieldIds: Set<string>
): ActiveFormDescriptorField[] => {
  const descriptor: ActiveFormDescriptorField[] = [];

  for (const fieldId of allFieldIds) {
    // Custom attributes register under a nested path (`CustomAttributeData.<key>`)
    // and use non-stable per-org keys — never prefillable in this cut.
    if (fieldId.startsWith('CustomAttributeData')) {
      continue;
    }
    if (NON_SCALAR_FIELD_KEYS.has(fieldId)) {
      continue;
    }

    const config = formConfig[fieldId];
    // No config entry → a derived/unknown field (e.g. `ancestorContributors`).
    if (!config) {
      continue;
    }

    const displayType = config.displayType?.displayType ?? 'text';
    if (!isSupportedType(displayType)) {
      continue;
    }

    descriptor.push({
      key: fieldId,
      label: config.formLabel,
      displayType,
      required: requiredFieldIds.has(fieldId),
    });
  }

  return descriptor;
};
